/**
 * Data Source Presets
 *
 * Pre-configured data sources for common use cases.
 * These make it trivially easy to link a dropdown to fleet data.
 *
 * Usage in form schema:
 * {
 *   type: "select",
 *   key: "vehicleId",
 *   label: "Select Vehicle",
 *   dataSource: { type: "preset", preset: "fleet-vehicles-active" }
 * }
 */

import { DataSourcePreset, DataSourcePresetId, DataSourceConfig, DataSourceOption } from './types';

// ============================================================================
// Compute Label Functions
// ============================================================================

/**
 * Named functions for computing labels from doc data.
 * Referenced by `computeLabel` in DataSourceConfig.
 * Also used by `computeField` in LinkedFilter for client-side filtering.
 *
 * Shared between client (useDataSource) and server (getPublicDataSourceOptions).
 */
export const COMPUTE_LABEL_FUNCTIONS: Record<string, (data: Record<string, unknown>) => string> = {
  /**
   * Construct composite client name from generator fields.
   * Replicates AppSheet formula:
   *   IF(ISNOTBLANK(ManagementCompany), ManagementCompany & " - ", "") &
   *   ClientName &
   *   IF(ISNOTBLANK(SiteName), " - " & SiteName, "")
   *
   * Examples:
   *   "Amentum - Intel - Mission"  (all three populated)
   *   "Amazon - SFO53"             (no management company)
   *   "101 Lombard Condominiums"   (no management company or site)
   */
  compositeClientName: (data) => {
    const mgmt = String(data['Management Company'] || '').trim();
    const client = String(data['Client Name'] || '').trim();
    const site = String(data['Site Name'] || '').trim();
    let result = '';
    if (mgmt) result += mgmt + ' - ';
    result += client;
    if (site) result += ' - ' + site;
    return result;
  },
};

// ============================================================================
// Preset Definitions
// ============================================================================

export const DATA_SOURCE_PRESETS: Record<DataSourcePresetId, DataSourcePreset> = {
  // Fleet Vehicles - All
  'fleet-vehicles': {
    id: 'fleet-vehicles',
    name: 'Fleet Vehicles (All)',
    description: 'All vehicles in the fleet including inactive',
    icon: '🚛',
    config: {
      collection: 'assets',
      // serverFetch (#6079): `assets` reads are Fleet (P7)-only in firestore.rules; the callable
      // serves every form filler and carries no assignee.
      serverFetch: true,
      labelTemplate: '{name}',
      valueField: 'assetId',
      includeFields: ['assetId', 'name', 'status', 'categoryId'],
      sortField: 'name',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading vehicles...',
      emptyPlaceholder: 'No vehicles found',
    },
  },

  // Fleet Vehicles - Active Only
  'fleet-vehicles-active': {
    id: 'fleet-vehicles-active',
    name: 'Fleet Vehicles (Active)',
    description: 'Only active vehicles in the fleet',
    icon: '🚛',
    config: {
      collection: 'assets',
      serverFetch: true, // see 'fleet-vehicles' above (#6079)
      labelTemplate: '{name}',
      valueField: 'assetId',
      includeFields: ['assetId', 'name', 'status', 'categoryId'],
      // No `categoryId == 'fleet-vehicles'` filter here (#7160). A Firestore equality filter cannot
      // express "bare OR org-prefixed" and the bare literal matched 0 of 57 live assets (31 carry
      // "org_onpoint_fleet-vehicles"). Vehicle-ness is decided by the server source
      // (functions/src/forms/dataSources/fleetVehicles.ts), which is the only path that can read
      // `assets` for a form filler (Fleet P7-only rules), hence serverFetch above.
      filters: [{ field: 'status', operator: '==', value: 'active' }],
      sortField: 'name',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading vehicles...',
      emptyPlaceholder: 'No active vehicles found',
    },
  },

  // Fleet Drivers - All (includes inactive, for historical lookups)
  'fleet-drivers': {
    id: 'fleet-drivers',
    name: 'Fleet Drivers (All)',
    description: 'All drivers including inactive (for historical data)',
    icon: '👤',
    config: {
      collection: 'appsheet_onpoint_users',
      labelTemplate: '{First Name} {Last Name}',
      valueField: 'Email',
      // Mobile is read directly by authenticated forms only; the PUBLIC endpoint strips it (#6079 D1(a), getPublicDataSourceOptions.ts).
      includeFields: ['Email', 'First Name', 'Last Name', 'Branch', 'Title', 'Mobile', 'Name', 'Inactive Date'],
      filters: [
        { field: 'Driver', operator: '==', value: 'Y' },
      ],
      sortField: 'Name',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading drivers...',
      emptyPlaceholder: 'No drivers found',
    },
  },

  // Fleet Drivers - Active Only (Status is Active)
  'fleet-drivers-active': {
    id: 'fleet-drivers-active',
    name: 'Fleet Drivers (Active)',
    description: 'Only currently employed drivers (Status is Active)',
    icon: '👤',
    config: {
      collection: 'appsheet_onpoint_users',
      labelTemplate: '{First Name} {Last Name}',
      valueField: 'Email',
      // Mobile is read directly by authenticated forms only; the PUBLIC endpoint strips it (#6079 D1(a), getPublicDataSourceOptions.ts).
      includeFields: ['Email', 'First Name', 'Last Name', 'Branch', 'Title', 'Mobile', 'Name', 'Inactive Date'],
      filters: [
        { field: 'Driver', operator: '==', value: 'Y' },
        // The AppSheet Status column decides who is current (measured 2026-10-21: Driver=Y rows are 22 "Active", 4
        // "Inactive"; none of them has an Inactive Date). "Active - ON CALL" counts as active, as in the codebase's own
        // test (isActiveAppsheetStatus). Named limit: a row with NO Status at all is left out (that test would keep it).
        { field: 'Status', operator: 'in', value: ['Active', 'Active - ON CALL'] },
      ],
      sortField: 'Name',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading drivers...',
      emptyPlaceholder: 'No active drivers found',
    },
  },

  // Customers - All (unique Client Names from service logs)
  'customers': {
    id: 'customers',
    name: 'Customers (All)',
    description: 'All customer names from service logs',
    icon: '🏢',
    config: {
      collection: 'appsheet_service_logs',
      labelField: 'Client Name',
      valueField: 'Client Name',
      includeFields: ['Client Name', 'Service ID'],
      deduplicate: true,
      loadingPlaceholder: 'Loading customers...',
      emptyPlaceholder: 'No customers found',
    },
  },

  // Customers - Active Only (composite names from generator records)
  // Constructs "Management Company - Client Name - Site Name" matching AppSheet formula
  'customers-active': {
    id: 'customers-active',
    name: 'Customers (Active)',
    description: 'Composite customer names from generator/equipment records',
    icon: '🏢',
    config: {
      collection: 'appsheet_generators',
      computeLabel: 'compositeClientName',
      computeLabelAsValue: true,
      includeFields: ['Client Name', 'Management Company', 'Site Name'],
      deduplicate: true,
      loadingPlaceholder: 'Loading customers...',
      emptyPlaceholder: 'No customers found',
    },
  },

  // Users
  'users': {
    id: 'users',
    name: 'Users',
    description: 'All users in the organization',
    icon: '👥',
    config: {
      collection: 'users',
      labelField: 'displayName',
      valueField: 'userId',
      includeFields: ['email', 'role'],
      sortField: 'displayName',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading users...',
      emptyPlaceholder: 'No users found',
    },
  },

  // Branches
  'branches': {
    id: 'branches',
    name: 'Branches',
    description: 'Organization branches/locations',
    icon: '📍',
    config: {
      collection: 'branches',
      labelField: 'name',
      valueField: 'id',
      includeFields: ['address', 'city', 'state'],
      sortField: 'name',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading branches...',
      emptyPlaceholder: 'No branches found',
    },
  },

  // Service Logs - for linking service tickets to forms
  'service-logs': {
    id: 'service-logs',
    name: 'Service Logs',
    description: 'Non-archived service tickets/jobs from AppSheet',
    icon: '📋',
    config: {
      collection: 'appsheet_service_logs',
      // ISS-431: sub-account ("unit id") identifies which unit the job is for.
      labelTemplate: '{Service ID} | {Client Name} | {Client Name Sub-Account} | {Short Description}',
      valueField: 'Service ID',
      includeFields: [
        'Service ID', 'Generator ID', 'Client Name', 'Client Name Sub-Account',
        'Short Description', 'Technician', 'Branch', 'Status', 'Service Type',
        'Other Type', 'Pre-Visit Notes', 'Equipment Type', 'Last Service URL',
        'Job Number', 'Related Service Parts', 'Created', 'Archive',
        'Service Address', 'Stage',
      ],
      filters: [
        { field: 'Archive', operator: '==', value: 'N' },
        // Stage filter handled in-memory after fetch — AppSheet stores Stage as
        // a string, and Firestore can't compare string fields with numeric values
      ],
      sortField: 'Created',
      sortDirection: 'desc',
      loadingPlaceholder: 'Loading service logs...',
      emptyPlaceholder: 'No service logs found',
    },
  },

  // Technicians - active technicians from user records
  'technicians': {
    id: 'technicians',
    name: 'Technicians',
    description: 'Active technicians from OnPoint user records',
    icon: '🔧',
    config: {
      collection: 'appsheet_onpoint_users',
      labelField: 'Technician_Name',
      valueField: 'Technician_Name',
      includeFields: ['Email', 'First Name', 'Last Name', 'Technician_Name', 'Mobile', 'Title'],
      filters: [
        { field: 'Technician', operator: '==', value: 'Y' },
        { field: 'Status', operator: '==', value: 'Active' },
      ],
      // No sortField — avoids composite index requirement with only ~15 technicians
      loadingPlaceholder: 'Loading technicians...',
      emptyPlaceholder: 'No technicians found',
    },
  },

  // Units by Customer - unique sub-accounts/units for a customer from generator records
  'units-by-customer': {
    id: 'units-by-customer',
    name: 'Units by Customer',
    description: 'Unique sub-accounts/units for a customer from generator records',
    icon: '🏗️',
    config: {
      collection: 'appsheet_generators',
      labelField: 'Client Name Sub-Account',
      valueField: 'Client Name Sub-Account',
      includeFields: ['Client Name Sub-Account', 'Client Name', 'Generator Address'],
      deduplicate: true,
      loadingPlaceholder: 'Loading units...',
      emptyPlaceholder: 'No units found',
    },
  },

  // Parts Catalog - price book from AppSheet (ISS-441 parts autocomplete)
  //
  // Corpus decision (measured 2026-09-03): appsheet_parts_list is the LIVE
  // catalog mirror (3,703 rows). The native copy servicePartsList held only
  // 800/3,703 rows with its AppSheet sync last writing 2025-12-09 — pointing
  // there would fail AppSheet parity by construction. Deliberately NO
  // "Not A Part" filter: AppSheet's own ref suggestions are unfiltered, and
  // 960 mirror rows lack the field entirely (an == "N" filter would silently
  // drop them).
  //
  // serverFetch is REQUIRED, not an optimization: the collection's firestore
  // rule allows client `list` only when resource.data.organizationId matches
  // the caller's org, and the org field is absent on every doc — a
  // client-direct query is denied. The authenticated callable reads under the
  // Admin SDK and projects away cost fields, so technicians receive part
  // numbers and descriptions, never OPG Cost / list prices.
  //
  // serverDocs (PR5b): the same callable answers with the projected rows
  // (gzip, keyed by part number, with a snapshot) so the device keeps a copy
  // and asks only for what changed (the 640 KB answer went to technicians'
  // phones 24 of 27 times in the 7 h after WAVE 98). With serverFetch set too,
  // a failure of the projected read falls back to the legacy options answer of
  // the SAME callable, never to a client-direct Firestore read (costs, doc ids).
  'parts-catalog': {
    id: 'parts-catalog',
    name: 'Parts Catalog (Price Book)',
    description: 'Part numbers and descriptions from the AppSheet parts catalog',
    icon: '🔩',
    config: {
      collection: 'appsheet_parts_list',
      serverFetch: true,
      serverDocs: 'parts-catalog',
      labelTemplate: '{Part Number} | {Part Description}',
      valueField: 'Part Number',
      includeFields: ['Part Number', 'Part Description'],
      sortField: 'Part Number',
      sortDirection: 'asc',
      loadingPlaceholder: 'Loading parts…',
      emptyPlaceholder: 'No matching parts',
    },
  },

  // Generators - equipment records from AppSheet
  'generators': {
    id: 'generators',
    name: 'Generators / Equipment',
    description: 'Generator and equipment records from AppSheet',
    icon: '⚡',
    config: {
      collection: 'appsheet_generators',
      serverDocs: 'generators',
      // ISS-431: sub-account ("unit id") is the only field that tells two
      // generators at the same customer + site apart.
      labelTemplate: '{Generator ID} | {Client Name} | {Client Name Sub-Account} | {Generator Make} {Generator Model}',
      valueField: 'Generator ID',
      includeFields: [
        'Generator ID', 'Generator Make', 'Generator Model', 'Generator Serial Number',
        'Generator KW', 'Generator Spec Number', 'Generator Type', 'Fuel', 'Voltage',
        'Engine Make', 'Engine Model', 'Engine Serial Number',
        'ATS Make', 'ATS Model', 'ATS Serial Number',
        'Alternator Make', 'Alternator Model', 'Alternator Serial Number',
        'ATS 2 Make', 'ATS 2 Model', 'ATS 2 Serial Number', 'ATS2',
        'Client Name', 'Client Name Sub-Account', 'Management Company', 'Site Name',
        'Generator Address', 'Generator Address City', 'Generator Address State',
        'Equipment Address Zip', 'Status', 'Branch',
      ],
      sortField: 'Generator ID',
      sortDirection: 'desc',
      loadingPlaceholder: 'Loading equipment...',
      emptyPlaceholder: 'No equipment found',
    },
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a preset by ID
 */
export function getPreset(presetId: DataSourcePresetId): DataSourcePreset | undefined {
  return DATA_SOURCE_PRESETS[presetId];
}

/**
 * Get all available presets
 */
export function getAllPresets(): DataSourcePreset[] {
  return Object.values(DATA_SOURCE_PRESETS);
}

/**
 * Presets whose options are a pure function of ANOTHER preset's options, so they share its one read
 * instead of making their own. The Field Service Report form declares `customers-active` AND
 * `generators`; both scanned the whole appsheet_generators collection (3,295 docs, 10.7 MB through the
 * Web SDK, which cannot project columns), i.e. the same 10.7 MB twice on every FSR open (measured
 * 2026-10-08). Only the PLAIN preset is derived: any override (filters, limit, ...) reads for itself.
 */
export const DERIVED_PRESETS: Partial<Record<DataSourcePresetId, {
  from: DataSourcePresetId;
  derive: (fromOptions: DataSourceOption[]) => DataSourceOption[];
}>> = {
  'customers-active': { from: 'generators', derive: deriveCustomersActiveFromGenerators },
};

const CUSTOMER_FIELDS = ['Client Name', 'Management Company', 'Site Name'] as const;

/**
 * The `customers-active` options, from the `generators` options. Reproduces the old own-read path
 * exactly: the composite client name is label AND value, the data carries the three name fields plus
 * the doc id and `_compositeClientName`, rows come in Firestore document-id order (the old path had no
 * sortField), and the first row per composite name wins. The generators preset carries all of these
 * fields (a test pins that), and its options are already free of soft-deleted rows.
 * @param {DataSourceOption[]} generatorOptions options of the `generators` preset
 * @return {DataSourceOption[]} the `customers-active` options
 */
export function deriveCustomersActiveFromGenerators(generatorOptions: DataSourceOption[]): DataSourceOption[] {
  const byId = [...generatorOptions].sort((a, b) => {
    const x = String(a.data?.id ?? '');
    const y = String(b.data?.id ?? '');
    return x < y ? -1 : x > y ? 1 : 0;
  });
  const seen = new Set<string>();
  const out: DataSourceOption[] = [];
  for (const opt of byId) {
    const data = opt.data ?? {};
    const composite = COMPUTE_LABEL_FUNCTIONS.compositeClientName(data);
    if (seen.has(composite)) continue;
    seen.add(composite);
    const optionData: Record<string, unknown> = { id: data.id };
    for (const f of CUSTOMER_FIELDS) if (data[f] !== undefined) optionData[f] = data[f];
    optionData._compositeClientName = composite;
    out.push({ label: composite, value: composite, data: optionData });
  }
  return out;
}

/**
 * Expand a preset into a full config
 */
export function expandPreset(presetId: DataSourcePresetId): DataSourceConfig | undefined {
  const preset = DATA_SOURCE_PRESETS[presetId];
  if (!preset) return undefined;

  return {
    type: 'collection',
    ...preset.config,
  };
}

/**
 * Get presets organized by category
 */
export function getPresetsByCategory(): Record<string, DataSourcePreset[]> {
  return {
    'Fleet': [
      DATA_SOURCE_PRESETS['fleet-vehicles-active'],
      DATA_SOURCE_PRESETS['fleet-vehicles'],
      DATA_SOURCE_PRESETS['fleet-drivers-active'],
      DATA_SOURCE_PRESETS['fleet-drivers'],
    ],
    'CRM': [
      DATA_SOURCE_PRESETS['customers-active'],
      DATA_SOURCE_PRESETS['customers'],
    ],
    'Service': [
      DATA_SOURCE_PRESETS['service-logs'],
      DATA_SOURCE_PRESETS['generators'],
      DATA_SOURCE_PRESETS['units-by-customer'],
      DATA_SOURCE_PRESETS['technicians'],
      DATA_SOURCE_PRESETS['parts-catalog'],
    ],
    'Organization': [
      DATA_SOURCE_PRESETS['users'],
      DATA_SOURCE_PRESETS['branches'],
    ],
  };
}
